import { compose, find, has, is, pathEq, prop } from 'ramda';

import { path } from './compact';
import { EN, FR, getLang, toPrismicLang } from './i18n';
import { getDomain } from './root';
import { getUrlFromDocument } from './routes/mapping';

/**
 * @typedef  {Object} PrismicAlternate
 * @property {string} type
 * @property {string} lang
 * @property {string} uid
 */

/**
 * @typedef  {Object} Location
 * @property {string} pathname
 */

/**
 * @typedef  {Object} Alternate
 * @property {string} to
 * @property {string} locale
 * @property {boolean} disabled
 */

/**
 * @typedef  {Object} Alternates
 * @property {Alternate} fr
 * @property {Alternate} en
 */

export const NOINDEX = 'noindex, nofollow';
export const NOINDEXATION = 'non';
// Make sure the indexation tag is available in production only

// we don 't care if indexation is enabled on preprod / acceptance since those
// are password protected pages.
/* istanbul ignore next */
const getIndex = () => 'index, follow';

export const INDEX = getIndex();

export const SEO_TITLE_SUFFIX = 'Le Groupe La Poste';

const hasMeta = compose(is(Object), prop('meta'));
const hasMetaTitle = compose(has('title'), prop('meta'));
const getMetaTitle = path('meta.title');
const getMetaDescription = path('meta.description');
const getMetaImage = path('meta.image');
const getMetaIndexed = pathEq(['meta', 'indexed'], NOINDEXATION);

const getCleanSEOTitle = (prefix) => `${prefix} | ${SEO_TITLE_SUFFIX}`;

export const getTitle =
  (suffix = true) =>
  (doc = null) => {
    if (!doc) {
      return SEO_TITLE_SUFFIX;
    }

    let docTitle = prop('title', doc);
    if (hasMeta(doc) && hasMetaTitle(doc)) {
      docTitle = getMetaTitle(doc);
    }

    if (is(String, docTitle) && docTitle !== SEO_TITLE_SUFFIX) {
      if (suffix) {
        docTitle = getCleanSEOTitle(docTitle);
      }

      return docTitle;
    }

    return SEO_TITLE_SUFFIX;
  };

export const getDescription = getMetaDescription;

export const getImage = getMetaImage;

export const getSeoText = prop('seoText');

export const getSeoLink = prop('seoLink');

/**
 * Function to extract the indexation config for the given document (default to "index, follow")
 *
 * @param {any} doc
 * @return {string|undefined}
 */
export const getIndexation = (doc) => (getMetaIndexed(doc) ? NOINDEX : INDEX);

/**
 * Look for an alternate language object that match the given locale.
 *
 * @param {string} locale
 * @param {Array.<PrismicAlternate>} alternates
 * @return {PrismicAlternate|undefined}
 */
const getAlternateForLang = (locale, alternates) =>
  find((alt) => alt && getLang(prop('lang', alt)) === locale)(alternates || []);

/**
 * Get an alternate language object that correspond to the given language for the given document
 *
 * @param {string} locale
 * @param {any} documentData
 * @param {Array.<PrismicAlternate>} alternates
 * @return {Alternate}
 */
const getDocumentAlternate = (locale, documentData, routeInfo) => {
  const prismicLang = toPrismicLang(locale);
  if (getLang(documentData?.lang) === locale) {
    return {
      to: getUrlFromDocument({ ...documentData, lang: prismicLang }, routeInfo),
      locale,
    };
  }

  const alternate = getAlternateForLang(
    locale,
    documentData?.alternateLanguages
  );
  if (alternate) {
    return {
      to: getUrlFromDocument(
        { ...documentData, ...alternate, lang: prismicLang },
        routeInfo
      ),
      locale,
    };
  }

  return { to: `/${locale}`, locale, disabled: true };
};

/**
 * Function to extract all alternative languages for the given document / location.
 *
 * @param {any} documentData
 * @return {Alternates}
 * @see Tests There a lot of examples in the unit tests, different cases are tested :
 * - no location
 * - no document
 * - no alternate languages in document
 * - etc...
 */
export const getAlternateLanguages = (documentData, routeInfo) => ({
  [FR]: getDocumentAlternate(FR, documentData, routeInfo),
  [EN]: getDocumentAlternate(EN, documentData, routeInfo),
});

export const getCanonical = (locale, document, routeInfo) => {
  const canonicalUrl = getAlternateLanguages(document, routeInfo)?.[locale]?.to;

  return canonicalUrl ? `${getDomain()}${canonicalUrl}` : null;
};
